import * as RadixTabs from "@radix-ui/react-tabs";
import type { ReactNode } from "react";

import { cn } from "../../shared/ui/cn";

export interface AdminTabItem {
  value: string;
  label: string;
  content: ReactNode;
}

/**
 * WAI-ARIA tabs (Radix: roving tabindex, arrow/Home/End keys). Radix mounts
 * only the active panel, so polling in inactive panels stops. The strip
 * scrolls horizontally on narrow screens; every tab is a 44px touch target.
 */
export function AdminTabs({
  items,
  value,
  onValueChange,
  label,
}: {
  items: AdminTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  label: string;
}) {
  return (
    <RadixTabs.Root
      value={value}
      onValueChange={onValueChange}
      className="flex min-w-0 flex-col gap-4"
    >
      <RadixTabs.List
        aria-label={label}
        className="flex min-w-0 gap-1 overflow-x-auto rounded-lg bg-sunken p-0.5"
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            className={cn(
              "min-h-11 min-w-11 shrink-0 whitespace-nowrap rounded-[11px] px-3.5 text-footnote font-semibold text-fg-muted",
              "transition-[background-color,color,box-shadow] motion-fast",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
              "data-[state=active]:bg-raised data-[state=active]:text-fg data-[state=active]:shadow-card",
            )}
          >
            {item.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((item) => (
        <RadixTabs.Content
          key={item.value}
          value={item.value}
          className="flex min-w-0 flex-col gap-[18px] focus-visible:outline-2 focus-visible:outline-focus"
        >
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
